import * as React from 'react';
import { useStableCallback } from './use-stable-callback';

type AsyncStatus = 'idle' | 'loading' | 'error' | 'success';

export const useAsync = <Param, Result>(
  asyncOperation: (param: Param) => Promise<Result>
) => {
  const loadedRef = React.useRef(false);

  React.useEffect(() => {
    loadedRef.current = true;

    () => {
      loadedRef.current = false;
    };
  }, []);

  const [state, dispatch] = React.useReducer(asyncReducer, {
    status: 'idle',
    data: undefined,
    error: undefined,
  });

  const makeAsyncCall = useStableCallback(asyncOperation);

  const run = React.useCallback(
    function runAsync(param: Param) {
      dispatch({
        type: 'loading',
      });

      return makeAsyncCall(param)
        .then(data => {
          if (loadedRef.current) {
            dispatch({
              type: 'loadData',
              data,
            });
          }
          return data;
        })
        .catch(error => {
          if (loadedRef.current) {
            dispatch({
              type: 'error',
              error,
            });
          }
        });
    },
    [makeAsyncCall]
  );

  return {
    run,
    state,
    status: state.status,
    data: state.data,
    isLoading: state.status === 'loading',
    isError: state.status === 'error',
    isSuccess: state.status === 'success',
  };
};

interface AsyncState<Data> {
  status: AsyncStatus;
  data: Data | undefined;
  error: any;
}

type AsyncAction<Data> =
  | {
      type: 'loading';
    }
  | {
      type: 'loadData';
      data: Data;
    }
  | {
      type: 'error';
      error: any;
    };

const asyncReducer = <Data>(
  state: AsyncState<Data>,
  action: AsyncAction<Data>
): AsyncState<Data> => {
  switch (action.type) {
    case 'loading':
      return {
        ...state,
        status: 'loading',
        error: undefined,
      };

    case 'loadData':
      return {
        ...state,
        status: 'success',
        error: undefined,
      };

    case 'error':
      return {
        ...state,
        status: 'error',
        error: action.error,
      };

    default:
      throw new Error(`Unimplemented: ${JSON.stringify(action, null, 2)}`);
  }
};
